"use client";

import cn from "@utils/cn";
import { getLink } from "@utils/linkUtils";
import { motion, useInView } from "framer-motion";
import { useLocale } from "next-intl";
import Image from "next/image";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import useBreakPoint from "@/hooks/useBreakpoint";
import { CompactModal as CompactModalType } from "@/types";

import { CompactModal } from "../CompactModal/CompactModal";
import RichText from "../RichText/RichText";
import TileCarouselContent from "../TileCarousel/TileCarouselContent";
import { TextBaseline as TextBaseLineType } from "../UnitGallery/type";
import Modal from "../ui/Modal/Modal";
import ModalContent from "../ui/Modal/ModalContent";
import "./TextBaseline.css";

export interface TextBaselineProps {
  entry: TextBaseLineType;
  animation?: boolean;
  indexKey?: number;
}

export const TextBaseline: FC<TextBaselineProps> = ({ entry, animation, indexKey }) => {
  const { isMd } = useBreakPoint("md");
  const locale = useLocale();
  const isExternalLink = entry.cta && entry.cta.link.linkType != "content";
  const fadeout = entry.ctaFadeout === undefined ? isExternalLink : entry.ctaFadeout;

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<CompactModalType | null>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(componentRef, { amount: 1 });
  const [hasAnimated, setHasAnimated] = useState(false);

  const openModal = (card: CompactModalType) => {
    setSelectedCard(card);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedCard(null);
    setModalOpen(false);
  };

  const fadeVariants = {
    visible: { opacity: 1, y: 0, transition: { delay: indexKey, duration: 1, ease: "easeInOut" } },
    hidden: { opacity: 0, y: 20 },
  };

  useEffect(() => {
    if (isInView && !hasAnimated && animation) {
      setHasAnimated(true);
    }
  }, [isInView, hasAnimated, animation]);

  const gridSize = useMemo(() => {
    let sizeClass: string;
    switch (entry.gridFlexibility) {
      case "6/12 columns":
        sizeClass = "md:w-6/12";
        break;
      case "4/12 columns":
        sizeClass = "md:w-4/12";
        break;
      case "full page":
        sizeClass = "md:w-20\\/24";
        break;
    }
    return sizeClass;
  }, [entry.gridFlexibility]);

  const imageBackground = useMemo(() => {
    if (entry.background) {
      return {
        backgroundImage: `url(${isMd ? entry.background.url : entry.backgroundMobile ? entry.backgroundMobile.url : entry.background.url})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      } as React.CSSProperties;
    } else {
      return undefined;
    }
  }, [isMd, entry.background, entry.backgroundMobile]);

  return (
    <>
      <motion.div
        ref={componentRef}
        className="pt-[40px]"
        style={imageBackground}
        initial={animation ? "hidden" : ""}
        animate={animation ? (hasAnimated ? "visible" : "hidden") : "visible"}
        variants={fadeVariants}
      >
        <div className={`mx-auto ${gridSize}`}>
          <div className={`flex flex-col px-m ${entry.contentAlignment === "center" ? "items-center text-center" : "items-start"}`}>
            {entry.logo && (
              <Image
                className={`${entry.contentAlignment === "center" ? "mx-auto" : " "}`}
                src={entry.logo.url}
                alt={entry.logo.title}
                width={entry.logo.width}
                height={entry.logo.height}
              />
            )}
            {entry.title && (
              <p className="font-normal uppercase font-primary text-l md:text-xl my-l accent-font accent-title accent-color">{entry.title}</p>
            )}
            {entry.description && <RichText content={entry.description} />}
            <div className="w-full">
              <div
                className={cn(
                  `${fadeout && !entry.secondaryCta ? "mt-[36px] md:mt-l w-full" : "mt-[40px]"}`,
                  `${!fadeout && "flex justify-center"}`,
                  "relative text-center",
                )}
              >
                {fadeout && (
                  <div
                    className={cn(
                      `${fadeout ? "absolute bottom-0 left-0 right-0 h-5xl backdrop-cta opacity-80 pointer-events-none z-0 w-full" : ""}`,
                    )}
                  ></div>
                )}
                {entry.cta && (
                  <Button
                    href={entry.modal ? "" : getLink(entry.cta, locale)}
                    target={entry.modal ? "" : entry.cta.link.target}
                    customClass={`${fadeout ? "relative z-10" : ""} font-medium`}
                    type="button"
                    size={ButtonSize.MEDIUM}
                    variant={ButtonVariant.PRIMARY}
                    theme={ButtonTheme.BLACK}
                    onClick={(e) => {
                      if (entry.modal) {
                        openModal(entry.modal);
                      }
                    }}
                  >
                    {entry.cta.label}
                  </Button>
                )}
                {entry.secondaryCta && (
                  <Button
                    href={entry?.secondaryCta?.link?.url}
                    size={ButtonSize.MEDIUM}
                    variant={ButtonVariant.SECONDARY}
                    theme={ButtonTheme.BLACK}
                    customClass={`${fadeout ? "relative z-10" : ""} ${entry.cta && "ml-m"} font-medium`}
                  >
                    {entry.secondaryCta.label}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      {selectedCard && entry.modal && (
        <CompactModal
          title={entry.modal.title}
          image={entry.modal.image}
          bodyTitle={entry.modal.bodyTitle}
          bodySubtitle={entry.modal.bodySubtitle}
          bodyText={entry.modal.bodyText}
          onClose={closeModal}
          cta={entry.modal.cta}
        ></CompactModal>
      )}
    </>
  );
};

export default TextBaseline;
